import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useDocumentTitle from '../../../components/useDocumentTitle';
import { client } from "../../../config/ipfsConfig";
import { collectionAdd, collectionDeploy } from '../../../hooks/Collectionhook';
import { useSelector } from 'react-redux';
import { Tooltip } from 'react-tooltip';
import { MdKeyboardBackspace } from "react-icons/md";
import { ToastContainer, toast } from 'react-toastify';
import { Puff } from 'react-loader-spinner';

const CollectionForm = () => {
    useDocumentTitle('Create Collection');
    const [fileUrl, setFileUrl] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [type, setType] = useState(null);
    const [formInput, updateFormInput] = useState({});
    const [gasLimitError, setGasLimitError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [autoClose, setAutoClose] = useState(false);

    const walletAddress = localStorage.getItem("WalletAddress");
    const Navigate = useNavigate();
    const PROVIDER = useSelector((state) => state.valueFromHeader);

    async function onChange(e) {
        const file = e.target.files[0];

        if (!file) return;  // Check if file is selected

        const fileType = file.type.split('/')[0]; // Get file type (image or video)
        const fileName = file.name;

        // Validate file type
        if (fileType !== 'image') {
            toast.error('Only image files are allowed.');
            setFileUrl(null); // Clear file URL if invalid file
            setFileName(null); // Clear file name
            return; // Stop further execution
        }

        // Validate file size (e.g., maximum 50MB)
        if (file.size > 50 * 1024 * 1024) {
            toast.error('File size must be less than 50MB.');
            setFileUrl(null); // Clear file URL if size exceeds limit
            setFileName(null); // Clear file name
            return; // Stop further execution
        }

        setType(fileType);
        setFileName(fileName);

        try {
            const added = await client.add(file, {
                progress: (prog) => console.log(`received: ${prog}`),
            });
            const url = `https://ipfs.infura.io/ipfs/${added.path}`;
            setFileUrl(url);
        } catch (error) {
            console.log("Error uploading file: ", error);
            toast.error('Error uploading file.');
            setFileUrl(null); // Clear URL on error
            setFileName(null); // Clear file name
        }
    }

    const closeButton = () => {
        setFileUrl(null);
        setFileName(null);
    };

    const handleChange = (e) => {
        updateFormInput({
            ...formInput,
            [e.target.name]: e.target.value,
        });
    };

    const handleChangeOfGas = (e) => {
        const valueOfGas = e.target.value;
        if (Number(valueOfGas) <= 4999999) {
            setGasLimitError('Gas limit must be greater than or equal 5,000,000.');
        } else {
            setGasLimitError('');
            updateFormInput({
                ...formInput,
                gasLimit: e.target.value,
            });
        }
    };

    const validateForm = () => {
        const errors = {};
        // Name validation
        if (!formInput.name || formInput.name.trim() === '') {
            errors.name = 'Collection name is required.';
        }

        // Symbol validation
        if (!formInput.symbol || formInput.symbol.trim() === '') {
            errors.symbol = 'Symbol is required.';
        }

        // Gas Limit validation
        if (!formInput.gasLimit || isNaN(formInput.gasLimit)) {
            errors.gasLimit = 'Please enter a valid gas limit.';
        } else if (Number(formInput.gasLimit) < 5000000) {
            errors.gasLimit = 'Gas limit must be at least 5,000,000.';
        }

        // File validation
        if (!fileUrl) {
            errors.file = 'Logo image is required.';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const CreateCollection = async () => {
        if (!walletAddress) {
            toast.error("Please Connect Wallet First");
            return;
        }

        // Validate form inputs before proceeding
        if (validateForm()) {

            await DeployCollection();
        } else {
            toast.error('Please correct the errors in the form.');
        }
    };

    const DeployCollection = async () => {
        const toastId = pendingToast("Processing for Collection Mint...");
        setAutoClose(false);

        try {
            const body = {
                walletAddress,
                gasLimit: formInput.gasLimit,
                name: formInput.name,
                symbol: formInput.symbol,
            };

            const { data } = await collectionDeploy(body);
            toast.update(toastId, { render: "Waiting for transaction confirmation..." });

            const signer = await PROVIDER.getSigner(walletAddress);
            const sentTx = await signer.sendTransaction(data);
            const receipt = await sentTx.wait();

            if (receipt) {
                const collectionData = {
                    collectionAddress: receipt.contractAddress,
                    walletAddress,
                    name: formInput.name,
                    symbol: formInput.symbol,
                    logoImg: fileUrl,
                    description: formInput.desc,
                };

                const response = await collectionAdd(JSON.stringify(collectionData));

                if (response.status === 201) {
                    toast.dismiss(toastId);
                    toast(<CustomSuccessToast contractAddress={receipt.contractAddress} message="Collection Created Successfully!" />);
                    setTimeout(() => Navigate(`/mint`), 2000);
                }
            }
        } catch (error) {
            toast.dismiss(toastId);
            setAutoClose(true);
            toast.error("Collection creation failed. Please try again.");
        }
    };

    const DraftCollection = async () => {
        if (validateForm()) {
            try {
                const data = JSON.stringify({
                    walletAddress: walletAddress,
                    name: formInput.name,
                    symbol: formInput.symbol,
                    logoImg: fileUrl,
                    isDraft: true,
                    gasLimit: formInput.gasLimit,
                    description: formInput.desc,
                });

                collectionAdd(data).then((data) => {
                    if (data.status === 201) {
                        toast.success("Save Collection Draft Successfully.");
                        Navigate("/profile");
                    }
                });
            } catch (error) {
                console.error("Error calling function:", error);
            }
        } else {
            toast.error('Please correct the errors in the form.');
        }
    };

    const isFormValid = () => {
        return (
            formInput.name &&
            formInput.symbol &&
            formInput.desc &&
            formInput.gasLimit &&
            fileUrl &&
            Number(formInput.gasLimit) > 4999999
        );
    };

    const handleBackClick = () => {
        Navigate(-1);
    };

    const pendingToast = (message) => {
        return toast(
            <div className="d-flex align-items-center">
                <Puff size={20} color="#CC0000" />
                <span className="ml-2">{message}</span>
            </div>,
            {
                position: "bottom-right",
                autoClose: false,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: false,
                progress: undefined,
            }
        );
    };

    const CustomSuccessToast = ({ contractAddress, closeToast, message }) => {
        const navigate = useNavigate();

        const handleViewDetails = () => {
            // Navigate to the NFT details page
            navigate(`/collectionDetails/${contractAddress}`);
            // Optionally close the toast after navigation
            closeToast();
        };

        return (
            <div>
                <p>{message}</p>
                <div className="d-flex align-items-center justify-content-between">
                    <button className='btn btn-sm btn-grad mr-2' onClick={handleViewDetails}>View Details</button>
                    {/* <button className='btn btn-sm btn-grad' onClick={handleViewOnEtherscan}>View on Etherscan</button> */}
                </div>
            </div>
        );
    };
    return (
        <div>

            <div className="container">
                <div className="container">
                    <button onClick={handleBackClick} className="btn btn-white btn-sm my-40">
                        <MdKeyboardBackspace className='mb-1' />  Back
                    </button>
                    <h1 className="title mb-40">Create Collection</h1>
                </div>
                <div className="box in__upload mb-60">
                    <div className="row">
                        <div className="col-lg-6">
                            <div className="left__part space-y-40 md:mb-20 upload_file" style={{ minHeight: "50vh" }}>
                                <div className="space-y-20">
                                    {fileUrl && (
                                        <div
                                            id="upload_file_close_button"
                                            className="closeButton"
                                            style={{ cursor: "pointer" }}
                                            onClick={closeButton}
                                        >
                                            X
                                        </div>
                                    )}
                                    {type === "image" && fileUrl && <img src={fileUrl} alt="" />}
                                    {type === "video" && fileUrl && <video src={fileUrl} />}
                                    {!fileUrl && (
                                        <p className="color_text">Max size: 50MB <br />JPG, PNG, GIF, SVG</p>
                                    )}
                                    {fileName && <div style={{ color: "black" }}>{fileName}</div>}
                                </div>
                                {!fileUrl && (
                                    <div className="space-y-20">
                                        <p className="color_text">or choose a file</p>
                                        <div id="browse files">
                                            <Link to="#" className="btn btn-white">
                                                Browse files
                                            </Link>
                                            <input type="file" onChange={onChange} />
                                        </div>
                                    </div>
                                )}
                                {validationErrors.file && <div style={{ color: 'red' }}>{validationErrors.file}</div>}
                            </div>
                        </div>
                        <div className="col-lg-6" style={{ position: "relative" }}>
                            <div className="form-group space-y-10">
                                <div className="space-y-20">
                                    <div className="space-y-10">
                                        <p className="nameInput">Name</p>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Collection Name"
                                            name="name"
                                            onChange={handleChange}
                                        />
                                        {validationErrors.name && (
                                            <div style={{ color: 'red' }}>{validationErrors.name}</div>
                                        )}
                                    </div>
                                    <div className="space-y-10">
                                        <p className="variationInput">Gas Limit</p>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Gas Limit"
                                            name="gasLimit"
                                            onChange={handleChangeOfGas}
                                        />
                                        {gasLimitError && <div style={{ color: 'red' }}>{gasLimitError}</div>}
                                        {validationErrors.gasLimit && (
                                            <div style={{ color: 'red' }}>{validationErrors.gasLimit}</div>
                                        )}
                                    </div>
                                    <div className="space-y-10">
                                        <p className="variationInput">Symbol</p>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Symbol"
                                            name="symbol"
                                            autoCapitalize="true"
                                            style={{ textTransform: "uppercase" }}
                                            onChange={handleChange}
                                        />
                                        {validationErrors.symbol && (
                                            <div style={{ color: 'red' }}>{validationErrors.symbol}</div>
                                        )}
                                    </div>
                                    <div className="space-y-10">
                                        <p className="nameInput">Description</p>
                                        <textarea
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. `Dtoc design art`"
                                            name="desc"
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="my-20" style={{ float: "right" }}>
                                <div onClick={() => DraftCollection()} className={`btn btn-grad btn_create mx-3 ${!isFormValid() ? "disabled" : ""}`}
                                    disabled={!isFormValid()} data-tooltip-id="draft-tooltip">
                                    Draft Collection
                                </div>

                                <div
                                    onClick={() => CreateCollection()}
                                    className={`btn btn-grad ${!isFormValid() ? "disabled" : ""}`}
                                    disabled={!isFormValid()}
                                    data-tooltip-id="create-tooltip"
                                >
                                    Create Collection
                                </div>
                                {!isFormValid() &&
                                    <>
                                        {/* Tooltip component */}
                                        <Tooltip id="draft-tooltip" place="top" effect="solid">
                                            Add all collection details
                                        </Tooltip>

                                        <Tooltip id="create-tooltip" place="top" effect="solid">
                                            Add all collection details
                                        </Tooltip>
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer
                position="bottom-right"
                newestOnTop={true}
                autoClose={false}
                closeOnClick={false}
                closeButton={autoClose}
                rtl={false}
                pauseOnFocusLoss={false}
                draggable={false}
            />
        </div>
    );
};

export default CollectionForm;
